import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Person from '@mui/icons-material/Person';
import Lock from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Close from '@mui/icons-material/Close';
import About from '@mui/icons-material/Info';
import { ProfileSuccessResponse } from '../../../api/authencation';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  useUpdateProfile,
  useChangePassword,
  useDeleteAccount,
} from '../../../hooks/useAuthencation';
import { ASSETS } from '../../../const/assets';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

// Password change validation schema
const changePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(32, 'Password must not exceed 32 characters')
    .matches(/[A-Z]/, 'At least one uppercase letter required')
    .matches(/[a-z]/, 'At least one lowercase letter required')
    .matches(/[0-9]/, 'At least one number required')
    .matches(/[\W_]/, 'At least one special character required')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  profile: ProfileSuccessResponse | undefined;
}

export default function SettingsDialog({
  open,
  onClose,
  profile,
}: SettingsDialogProps) {
  const [tabValue, setTabValue] = useState(0);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Add state for profile form
  const [firstName, setFirstName] = useState(profile?.firstName || '');
  const [lastName, setLastName] = useState(profile?.lastName || '');

  // Update local state when profile changes
  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
    }
  }, [profile]);

  const { mutateAsync: updateProfileMutation, isPending } = useUpdateProfile();

  const {
    mutateAsync: changePasswordMutation,
    isPending: isChangePasswordPending,
  } = useChangePassword();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChangePassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    // Here you would implement the API call to change password
    await changePasswordMutation({
      oldPassword: values.currentPassword,
      newPassword: values.newPassword,
      confirmNewPassword: values.confirmPassword,
    });
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    await updateProfileMutation({
      firstName,
      lastName,
    });
  };

  const tabList = [
    {
      label: 'Profile',
      icon: <Person />,
    },
    {
      label: 'Change Password',
      icon: <Lock />,
    },
    {
      label: 'About',
      icon: <About />,
    },
  ];
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAuthenticated');

    window.location.href = '/main-login';
  };

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [openDeleteAccountDialog, setOpenDeleteAccountDialog] = useState(false);
  const {
    mutateAsync: deleteAccountMutation,
    isPending: isDeleteAccountPending,
  } = useDeleteAccount();

  const handleDeleteAccount = () => {
    deleteAccountMutation();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          className: '!rounded-2xl overflow-hidden',
        },
      }}
    >
      <DialogTitle className="flex justify-between items-center  text-black font-medium border-b border-gray-200">
        Settings
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Box
        sx={{
          display: { xs: 'block', md: 'flex' },
          maxHeight: { xs: '80vh', md: 'auto' },
          overflow: { xs: 'auto', md: 'visible' },
        }}
      >
        <Box
          sx={{
            borderRight: { xs: 0, md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              fontSize: '16px',
              padding: '16px 20px',
              color: '#7A7A7A',
              textTransform: 'uppercase',
            }}
          >
            Account
          </Box>
          <Tabs
            orientation="horizontal"
            variant="scrollable"
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
            sx={{
              minWidth: { xs: '100%', md: '240px' },
              minHeight: { xs: 'auto', md: '400px' },
              '& .MuiTabs-indicator': {
                display: { xs: 'block', md: 'none' },
                height: { xs: '2px', md: '0px !important' },
              },
              '& .MuiTabs-flexContainer': {
                justifyContent: { xs: 'space-around', md: 'flex-start' },
              },
              '& .MuiTabs-list': {
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
              },
              '& .MuiTab-root': {
                minWidth: { xs: '60px', md: '240px' },
              },
            }}
          >
            {tabList.map((tab, index) => (
              <Tab
                key={index}
                label={<span className="hidden md:inline">{tab.label}</span>}
                icon={tab.icon}
                iconPosition="start"
                {...a11yProps(index)}
                sx={{
                  textTransform: 'none',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  height: '48px',
                  padding: { xs: '0px 8px', md: '0px 28px' },
                  minHeight: '48px',
                  '& .MuiTab-iconWrapper': {
                    marginRight: { xs: 0, md: 1 },
                  },
                  '& .MuiTab-labelIcon': {
                    minHeight: 'auto',
                  },
                  '&.Mui-selected': {
                    backgroundColor: { xs: 'transparent', md: '#f0f0f0' },
                    border: 'none',
                    color: 'black',
                  },
                }}
              />
            ))}
          </Tabs>
          <Box
            className="md:flex items-center gap-3 cursor-pointer px-7 py-4 mb-3 hidden"
            sx={{
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
            onClick={() => {
              setOpenLogoutDialog(true);
            }}
          >
            <img src={ASSETS.icLogout} alt="logout" />
            <span className="text-[#E64646] leading-6 font-medium">Logout</span>
          </Box>
          <Dialog
            open={openLogoutDialog}
            onClose={() => setOpenLogoutDialog(false)}
            slotProps={{
              paper: {
                className: '!rounded-2xl overflow-hidden',
              },
            }}
          >
            <DialogTitle className="text-black font-bold flex items-center justify-center !pb-2">
              Logout
            </DialogTitle>
            <DialogContent className="mt-4">
              <DialogContentText className="text-black px-5">
                Are your 100% sure you want to logout
              </DialogContentText>
            </DialogContent>
            <DialogActions className="p-4 flex gap-2 !justify-center mb-3">
              <Button
                onClick={() => setOpenLogoutDialog(false)}
                className="text-zinc-600 hover:text-zinc-800 normal-case px-4"
                variant="text"
                sx={{
                  textTransform: 'none',
                  color: 'black',
                  borderRadius: '100px',
                  border: '1px solid #B9BCC4',
                  px: 4,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setOpenLogoutDialog(false);
                  handleLogout();
                }}
                variant="contained"
                sx={{
                  backgroundColor: '#E64646',
                  color: 'white',
                  borderRadius: '100px',
                  border: '1px solid #E64646',
                  px: 3,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#E64646',
                  },
                }}
              >
                Confirm Logout
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
        <DialogContent className="!p-0 md:!p-4">
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ my: { xs: 0, md: 2 } }}>
              <TextField
                label="Email"
                value={profile?.email || ''}
                fullWidth
                margin="normal"
                disabled
              />
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Credit"
                value={profile?.credit || 0}
                fullWidth
                margin="normal"
                disabled
              />
              <Box
                sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}
              >
                <Button
                  variant="text"
                  color="error"
                  sx={{
                    textTransform: 'none',
                    color: '#FF0000',
                    fontSize: { xs: '12px', md: '16px' },
                  }}
                  startIcon={<img src={ASSETS.icDelete} alt="delete" />}
                  onClick={() => setOpenDeleteAccountDialog(true)}
                >
                  <span className="text-[#E64646] leading-6">
                    Delete Account
                  </span>
                </Button>
                <Dialog
                  open={openDeleteAccountDialog}
                  onClose={() => setOpenDeleteAccountDialog(false)}
                  slotProps={{
                    paper: {
                      className: '!rounded-2xl overflow-hidden',
                    },
                  }}
                >
                  <DialogTitle className="text-black font-bold flex items-center justify-center !pb-2">
                    Delete Account
                  </DialogTitle>
                  <DialogContent className="mt-4">
                    <DialogContentText className="text-black px-5">
                      Are your 100% sure you want to delete your account?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions className="p-4 flex gap-2 !justify-center mb-3">
                    <Button
                      onClick={() => setOpenDeleteAccountDialog(false)}
                      className="text-zinc-600 hover:text-zinc-800 normal-case px-4"
                      variant="text"
                      sx={{
                        textTransform: 'none',
                        color: 'black',
                        borderRadius: '100px',
                        border: '1px solid #B9BCC4',
                        px: 4,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        setOpenDeleteAccountDialog(false);
                        handleDeleteAccount();
                      }}
                      variant="contained"
                      sx={{
                        backgroundColor: '#E64646',
                        color: 'white',
                        borderRadius: '100px',
                        border: '1px solid #E64646',
                        px: 3,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#E64646',
                        },
                      }}
                      loading={isDeleteAccountPending}
                    >
                      Confirm Delete
                    </Button>
                  </DialogActions>
                </Dialog>
                <Button
                  variant="contained"
                  onClick={handleUpdateProfile}
                  disabled={isPending}
                  loading={isPending}
                  sx={{
                    textTransform: 'none',
                    background: 'linear-gradient(to right, #6a11cb, #2575fc)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(to right, #5910b0, #2065d8)',
                    },
                    fontSize: { xs: '12px', md: '16px' },
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Formik
              initialValues={{
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              }}
              validationSchema={changePasswordSchema}
              onSubmit={handleChangePassword}
            >
              {({ handleChange, handleBlur, errors, touched }) => (
                <Form>
                  <Box sx={{ my: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Change Password
                    </Typography>
                    <Field
                      as={TextField}
                      name="currentPassword"
                      label="Current Password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.currentPassword &&
                        Boolean(errors.currentPassword)
                      }
                      helperText={
                        touched.currentPassword && errors.currentPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <div
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              className="cursor-pointer"
                            >
                              {showCurrentPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </div>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      name="newPassword"
                      label="New Password"
                      type={showNewPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <div
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              className="cursor-pointer"
                            >
                              {showNewPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </div>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <div
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="cursor-pointer"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </div>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box
                      sx={{
                        mt: 3,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isChangePasswordPending}
                        loading={isChangePasswordPending}
                        sx={{
                          textTransform: 'none',
                          background:
                            'linear-gradient(to right, #6a11cb, #2575fc)',
                          color: 'white',
                          '&:hover': {
                            background:
                              'linear-gradient(to right, #5910b0, #2065d8)',
                          },
                        }}
                      >
                        Change Password
                      </Button>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Box className="flex flex-col gap-4">
              <Box
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => window.open('https://policy.aife.app', '_blank')}
              >
                <img src={ASSETS.icLink} alt="link" />
                <span className="text-black leading-6">Term of Use</span>
              </Box>
              <Box
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => window.open('https://policy.aife.app', '_blank')}
              >
                <img src={ASSETS.icLink} alt="link" />
                <span className="text-black leading-6">Privacy Policy</span>
              </Box>
            </Box>
          </TabPanel>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
